import React, { SetStateAction, useCallback, useEffect, useState } from 'react'
import {
  OptionItemInterface,
  SaleItemInterface,
  StockDocumentInterface,
  StoreInterface,
  ValidationResultInterface,
  ValidationRulesInterface,
} from '../../../../../../common/types/interfaces'
import { getSalesApi } from '../../../../../../salesApi/salesApi'
import { useValidation } from '../../../../../../common/hooks/useValidation'
import { useInput } from '../../../../../../common/hooks/useInput'
import { useCommonLoader } from '../../../../../../common/hooks/useCommonLoader'
import { delayAction } from '../../../../../../common/utils/functions/delayAction'
import { stockDocumentValidations } from '../../../../../../common/utils/constants'
import { processAttributes } from '../../../../../../common/utils/functions/processAttributes'
import { createSelectList } from '../../../../../../common/utils/functions/createSelectList'

const validations: ValidationRulesInterface[] = stockDocumentValidations

export const useForm = (
  document: Partial<StockDocumentInterface>,
  setDocument: React.Dispatch<SetStateAction<Partial<StockDocumentInterface>>>,
  edit: boolean,
  getList: () => void,
  setModalActive: React.Dispatch<SetStateAction<boolean>>,
  documentType: number,
) => {
  const [salesApi] = useState(getSalesApi())
  const [validationResult, setValidationResult] = useState<ValidationResultInterface[]>([])
  const [isFormValid, setIsFormValid] = useState<boolean>(false)
  const [storeList, setStoreList] = useState<StoreInterface[]>([])
  const [saleItemList, setSaleItemList] = useState<SaleItemInterface[]>([])
  const [dirtyFields, setDirtyFields] = useState<Set<string>>(new Set())

  const { validate, checkAndAddValidation, setDirty } = useValidation(
    setValidationResult,
    validationResult,
    validations,
  )

  const markFieldDirty = useCallback((fieldPath: string) => {
    setDirtyFields((prev) => new Set([...prev, fieldPath]))
  }, [])

  const { inputHandler } = useInput(setDocument)
  const { showCommonLoader, hideCommonLoader } = useCommonLoader()

  const getValidationResult = (fieldName: string) => {
    return validationResult.find((el) => el.name === fieldName)
  }

  const getStoreList = useCallback(async () => {
    try {
      const result = await salesApi.getStoreList()
      if (result) setStoreList(result?.data || [])
    } catch (error) {
      console.error('Error loading stores:', error)
    }
  }, [salesApi])

  const getSaleItemList = useCallback(async () => {
    try {
      const result = await salesApi.getSaleItemList()
      if (result) setSaleItemList(result?.data || [])
    } catch (error) {
      console.error('Error loading items:', error)
    }
  }, [salesApi])

  const createStoreSelectList = useCallback(() => {
    return createSelectList(storeList)
  }, [storeList])

  const createSaleItemSelectList = useCallback(() => {
    return saleItemList.map((item) => ({
      id: String(item.id),
      label:'#' +
        item.id +
        ' ' + item.name + (item.attributes?.length ? ' ' + processAttributes(item.attributes) : ''),
      name: item.name,
      value: String(item.id),
    }))
  }, [saleItemList])

const handleMovementChange = (index: number, field: string, value: string | number): void => {
  if (field === 'count') {
    const strValue = String(value)
    // Только цифры
    let filtered = strValue.replace(/\D/g, '')
    // Убираем ведущие нули
    filtered = filtered.replace(/^0+/, '')
    // Ограничение длины
    if (filtered.length > 7) filtered = filtered.slice(0, 7)
    
    setDocument((prev) => ({
      ...prev,
      movements: prev.movements?.map((item, i) =>
        i === index ? { ...item, [field]: filtered } : item,
      ),
    }))
  } else {
    setDocument((prev) => ({
      ...prev,
      movements: prev.movements?.map((item, i) =>
        i === index ? { ...item, [field]: value } : item,
      ),
    }))
  }
}

  // В хуке
  const getItemValidationResult = useCallback(
    (index: number, field: 'saleItemId' | 'count') => {
      const value = document.movements?.[index]?.[field]
      const fieldPath = `movements[${index}].${field}`

      if (!value) {
        return {
          name: fieldPath,
          isEmptyError: true,
          isDirty: false,
          isEmpty: true,
          notValidFormat: false,
          tooShort: false,
          tooLong: false,
          notValid: false,
        } as ValidationResultInterface
      }

      return undefined
    },
    [document.movements],
  )

  const formAction = useCallback(async () => {
    if (!isFormValid) return

    try {
      await showCommonLoader()
      await delayAction(1000)

      const payload = {
        type: documentType,
        number: document.number?.trim() || '',
        storeId: Number(document.storeId),
        comment: document.comment || '',
        items:
          document.movements?.map((m) => ({
            saleItemId: Number(m.saleItemId),
            count: Number(m.count),
          })) || [],
      }

      if (documentType === 0) {
        await salesApi.createReceipt(payload)
      } else {
        await salesApi.createWriteOff(payload)
      }

      await getList()
      await hideCommonLoader()
      setModalActive(false)
    } catch (error) {
      await hideCommonLoader()
    }
  }, [
    isFormValid,
    document,
    documentType,
    salesApi,
    getList,
    setModalActive,
    showCommonLoader,
    hideCommonLoader,
  ])

  useEffect(() => {
    getStoreList()
    getSaleItemList()
  }, [getStoreList, getSaleItemList])

  useEffect(() => {
    for (const key in document) {
      if (validations.some((v) => v.fieldName === key)) {
        checkAndAddValidation(key)
      }
    }
  }, [document, checkAndAddValidation])

  useEffect(() => {
    for (const key in document) {
      if (validations.some((v) => v.fieldName === key)) {
        validate(key, document[key])
      }
    }
  }, [document])

  useEffect(() => {
    const hasErrors = validationResult.some((el) => el.notValid)
    const hasItems = document.movements && document.movements.length > 0
    const itemsValid =
      document.movements?.every((m) => m.saleItemId && m.count && Number(m.count) > 0) ?? false

    setIsFormValid(Boolean(!hasErrors && hasItems && itemsValid))
  }, [validationResult, document.movements])

  return {
    formAction,
    inputHandler,
    setDirty,
    getValidationResult,
    createStoreSelectList,
    createSaleItemSelectList,
    disabled: !isFormValid,
    handleMovementChange,
    getItemValidationResult,
    markFieldDirty,
  }
}
